// Proactive stale-bundle detection — root-cause fix for the stale-deploy
// chunk failures (Sentry KL-7/KL-7N/KL-7R/KL-9Q).
//
// Route chunks are content-hashed and deleted on every deploy, so a
// long-lived tab on the previous bundle breaks its next lazy chunk load.
// Instead of recovering after the crash, know about the deploy up front:
// poll /version (the server's build SHA, same CI-injected value as our
// own VUE_APP_SENTRY_RELEASE) and, once a newer version is known, turn
// the next navigation into a full page load — fresh index.html, fresh
// chunk hashes. The user experiences it as a normal (slightly harder)
// navigation they themselves initiated, at most once per deploy.

export const CHECK_MIN_INTERVAL_MS = 60 * 1000;
// Stale detection only needs to beat the user's next navigation, not the
// first paint — keep the boot path free of the extra request.
export const INITIAL_CHECK_DELAY_MS = 15 * 1000;
const POLL_INTERVAL_MS = 10 * 60 * 1000;
const STORAGE_KEY = 'version-reloaded-for';

// The latest mismatching server SHA seen, or null.
let serverVersion = null;
let lastCheckAt = 0;

// Read lazily so local dev (no release SHA baked in) disables the whole
// mechanism, and tests can control it via process.env.
function ownVersion() {
  return process.env.VUE_APP_SENTRY_RELEASE || '';
}

function watchDisabled() {
  // PR previews are a different build served path-prefixed on the shared
  // test origin, so /version there answers with the test deploy's SHA — a
  // permanent mismatch that would force-reload testers out of the preview.
  return !ownVersion() || process.env.VUE_APP_SENTRY_ENV === 'pr-test';
}

export async function checkVersion({
  fetchImpl = window.fetch.bind(window),
  now = Date.now()
} = {}) {
  if (watchDisabled()) return;
  if (now - lastCheckAt < CHECK_MIN_INTERVAL_MS) return;
  lastCheckAt = now;

  try {
    const res = await fetchImpl('/version', {
      cache: 'no-store',
      headers: { accept: 'application/json' }
    });
    if (!res.ok) return;

    const data = await res.json();
    if (data && data.version && data.version !== ownVersion()) {
      serverVersion = data.version;
    }
  } catch (e) {
    // Offline, endpoint missing (older server), proxy quirks — a failed
    // check must never affect the app.
  }
}

// sessionStorage can be unavailable (Safari private mode) — fall back to a
// module variable so the guard still holds for the lifetime of the page.
let memoryReloadedFor = null;

function readReloadedFor() {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) || memoryReloadedFor;
  } catch (e) {
    return memoryReloadedFor;
  }
}

function writeReloadedFor(version) {
  memoryReloadedFor = version;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, version);
  } catch (e) {
    // memoryReloadedFor already covers this page's lifetime.
  }
}

// Only ever hand location.assign a same-origin absolute path: a single
// leading slash, not '//' or '/\' (scheme-relative → off-origin redirect).
// A route like /login?returnUrl=//evil.com otherwise reaches here as the
// catch-all route's raw fullPath.
function isSafeInternalPath(path) {
  return typeof path === 'string' && /^\/(?!\/|\\)/.test(path);
}

// Called from the router's beforeEach. Returns the fullPath to hard-load,
// or null to let the SPA navigation proceed. Keyed on the detected server
// SHA, so the degraded mode when the shell itself is served stale (broken
// cache layer) is bounded to exactly ONE forced load per deploy — never a
// reload loop.
export function consumeForcedReloadPath(to, from) {
  if (!serverVersion) return null;

  const target = to.fullPath;
  if (target === from.fullPath) return null;
  if (!isSafeInternalPath(target)) return null;
  if (readReloadedFor() === serverVersion) return null;

  writeReloadedFor(serverVersion);
  return target;
}

export function startVersionWatch({
  fetchImpl,
  intervalMs = POLL_INTERVAL_MS
} = {}) {
  if (watchDisabled()) return function stop() {};

  const check = () => checkVersion({ fetchImpl });
  const initialTimer = setTimeout(check, INITIAL_CHECK_DELAY_MS);
  const interval = setInterval(check, intervalMs);
  const onVisibilityChange = () => {
    // Long-backgrounded tabs are exactly the ones that go stale — check as
    // soon as the user comes back.
    if (document.visibilityState === 'visible') check();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  return function stop() {
    clearTimeout(initialTimer);
    clearInterval(interval);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}

export function __resetVersionWatchForTests() {
  serverVersion = null;
  lastCheckAt = 0;
  memoryReloadedFor = null;
}
